import React, { useEffect } from "react";
import {
  PawPrint, ShieldCheck, Home, Users, Heart, HeartHandshake,
  Phone, Mail, MapPin, Instagram, ChevronDown,
} from "lucide-react";

/**
 * STATYCZNA MAKIETA (US market) — Birch Haus, German & Australian Shepherds.
 * Izolowany podgląd do akceptacji designu przez klienta. Bez backendu.
 * Trasa: /birch-haus
 */

const IMG = "/birchhaus";

const FONT_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=Cinzel:wght@500;600;700&family=Dancing+Script:wght@500;600;700&family=Jost:wght@300;400;500;600&display=swap');
.bh-root{--navy:#0e1d2c;--navy2:#13283b;--gold:#b88a3d;--gold-soft:#c9a35a;--cream:#f8f2e7;--cream2:#f3ead9;--ink:#384049;}
.bh-root, .bh-sans{font-family:'Jost',-apple-system,sans-serif;}
.bh-serif{font-family:'Playfair Display',Georgia,serif;}
.bh-cinzel{font-family:'Cinzel',Georgia,serif;}
.bh-script{font-family:'Dancing Script',cursive;}
.bh-track{letter-spacing:.18em;}
.bh-track-sm{letter-spacing:.12em;}
.bh-fade{opacity:0;transform:translateY(18px);animation:bhUp .8s cubic-bezier(.2,.7,.2,1) forwards;}
@keyframes bhUp{to{opacity:1;transform:none;}}
.bh-link{position:relative;}
.bh-link::after{content:"";position:absolute;left:0;bottom:-6px;height:2px;width:0;background:var(--gold);transition:width .3s ease;}
.bh-link:hover::after{width:100%;}
.bh-link-active::after{width:100%;}
.bh-btn{transition:transform .25s ease, box-shadow .25s ease, background-color .25s ease, color .25s ease;}
.bh-btn:hover{transform:translateY(-2px);}
.bh-card{transition:transform .35s cubic-bezier(.2,.7,.2,1), box-shadow .35s ease;}
.bh-card:hover{transform:translateY(-6px);box-shadow:0 24px 50px -24px rgba(14,29,44,.45);}
.bh-imgzoom{transition:transform .7s cubic-bezier(.2,.7,.2,1);}
.bh-imgwrap:hover .bh-imgzoom{transform:scale(1.06);}
`;

const GoldRule = ({ center = false }) => (
  <div className={`flex items-center gap-2 ${center ? "justify-center" : ""} my-4`}>
    <span className="h-px w-12" style={{ background: "var(--gold)" }} />
    <Heart size={13} style={{ color: "var(--gold)" }} fill="var(--gold)" />
    {center && <span className="h-px w-12" style={{ background: "var(--gold)" }} />}
  </div>
);

const Btn = ({ variant = "gold", children, className = "", icon = false, testid }) => {
  const base =
    "bh-btn inline-flex items-center justify-center gap-2 px-7 py-3.5 text-[12.5px] font-semibold bh-track-sm uppercase rounded-sm cursor-pointer select-none";
  const styles = {
    gold: { background: "var(--gold)", color: "#fff", boxShadow: "0 14px 30px -16px rgba(184,138,61,.9)" },
    navy: { background: "var(--navy)", color: "#fff" },
    outline: { background: "transparent", color: "var(--navy)", border: "1.5px solid var(--navy)" },
    ghostGold: { background: "transparent", color: "var(--gold)", border: "1.5px solid var(--gold)" },
  };
  return (
    <button data-testid={testid} className={`${base} ${className}`} style={styles[variant]}>
      {icon && <PawPrint size={15} />}
      {children}
    </button>
  );
};

const SectionTitle = ({ children, center = true }) => (
  <div className={center ? "text-center" : ""}>
    <h3 className="bh-serif text-2xl md:text-[26px] font-bold tracking-wide" style={{ color: "var(--navy)" }}>
      {children}
    </h3>
    <GoldRule center={center} />
  </div>
);

const NAV = ["HOME", "ABOUT", "OUR DOGS", "PUPPIES", "SOCIALIZATION", "REVIEWS", "FAQ", "CONTACT"];

export default function BirchHausPreview() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Birch Haus — Family-Raised German & Australian Shepherds | USA";
    return () => { document.title = prev; };
  }, []);
  return (
    <div className="bh-root min-h-screen py-0 sm:py-7 lg:py-10" style={{ background: "var(--navy)", color: "var(--ink)" }} data-testid="birchhaus-page">
      <style>{FONT_CSS}</style>

      <div className="mx-auto w-full max-w-[1440px] px-0 sm:px-5 lg:px-6">
        <div className="overflow-hidden sm:rounded-2xl shadow-[0_40px_90px_-30px_rgba(0,0,0,.7)]" style={{ background: "var(--cream)" }} data-testid="bh-board">

      {/* ============ NAV (full-width cream, elegant single row) ============ */}
      <header className="relative z-20" style={{ background: "var(--cream)", borderBottom: "1px solid rgba(184,138,61,.22)" }} data-testid="bh-navbar">
        <div className="mx-auto max-w-[1340px] px-5 lg:px-10 flex items-center justify-between gap-6 min-h-[124px] lg:min-h-[166px]">
          <a href="#top" className="flex items-center shrink-0 bh-fade" data-testid="bh-logo">
            <img src={`${IMG}/00_logo_original_birch_haus_transparent_bg.png`} alt="Birch Haus — German & Australian Shepherds" className="h-[104px] lg:h-[150px] w-auto object-contain" />
          </a>
          <nav className="hidden lg:flex items-center gap-6 xl:gap-7 bh-sans" data-testid="bh-nav">
            {NAV.map((n) => {
              const active = n === "HOME";
              return (
                <a key={n} href="#top"
                   className={`bh-link flex items-center gap-1 whitespace-nowrap text-[12.5px] bh-track-sm font-semibold transition-opacity ${active ? "bh-link-active" : "hover:opacity-60"}`}
                   style={{ color: active ? "var(--navy)" : "rgba(56,64,73,.92)" }}>
                  {n}
                  {(n === "OUR DOGS" || n === "PUPPIES") && <ChevronDown size={13} className="opacity-60" />}
                </a>
              );
            })}
          </nav>
          <Btn variant="gold" icon testid="bh-apply-top" className="!px-6 !py-3.5 shrink-0">APPLY FOR A PUPPY</Btn>
        </div>
      </header>

      <main id="top">
        {/* ============ HERO ============ */}
        <section className="relative overflow-hidden" data-testid="bh-hero">
          <div className="relative w-full bg-cover bg-center" style={{ backgroundImage: `url(${IMG}/hero_bg.png)` }} data-testid="bh-hero-bg">
            <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, var(--cream) 0%, rgba(248,242,231,.92) 30%, rgba(248,242,231,.45) 50%, rgba(248,242,231,0) 72%)" }} />
            <div className="relative mx-auto max-w-[1280px] px-5 lg:px-8">
              <div className="max-w-[560px] py-20 lg:py-32 min-h-[540px] lg:min-h-[720px] flex flex-col justify-center bh-fade">
                <span className="text-[13px] bh-track font-semibold uppercase" style={{ color: "var(--gold)" }}>
                  Family &nbsp;•&nbsp; Love &nbsp;•&nbsp; Loyalty
                </span>
                <h1 className="bh-serif mt-5 text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] font-bold" style={{ color: "var(--navy)" }}>
                  Family-Raised<br />German &amp; Australian<br />Shepherds in the USA
                </h1>
                <GoldRule />
                <p className="max-w-md text-[15.5px] leading-relaxed text-[var(--ink)]/85">
                  Our dogs are more than our passion — they are family. Raised in our home
                  with love, socialization and a commitment to health &amp; excellence.
                </p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <Btn variant="gold" icon testid="bh-hero-puppies">Available Puppies</Btn>
                  <Btn variant="outline" testid="bh-hero-dogs">Meet Our Dogs</Btn>
                </div>
              </div>
            </div>
          </div>

          {/* ===== TRUST BAR ===== */}
          <div className="mx-auto max-w-[1280px] px-5 lg:px-8 relative -mt-2 lg:-mt-10 pb-2">
            <div className="rounded-md grid grid-cols-2 lg:grid-cols-4 gap-y-7 px-7 py-8 lg:py-7"
                 style={{ background: "var(--navy)" }} data-testid="bh-trustbar">
              {[
                { icon: ShieldCheck, t: "Health Tested", d: "Genetic testing, OFA/PennHIP & more" },
                { icon: Home, t: "Home-Raised", d: "Raised in our home, not in kennels" },
                { icon: Users, t: "Early Socialization", d: "Daily exposure, handling, children & new experiences" },
                { icon: HeartHandshake, t: "Lifetime Support", d: "We're here for you for the life of your dog" },
              ].map((f, i) => (
                <div key={f.t} className={`flex items-start gap-3.5 px-2 lg:px-5 ${i !== 0 ? "lg:border-l lg:border-white/10" : ""}`}>
                  <f.icon size={28} strokeWidth={1.4} style={{ color: "var(--gold)" }} className="shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[13px] bh-track-sm font-semibold uppercase" style={{ color: "var(--gold-soft)" }}>{f.t}</div>
                    <div className="text-[13px] leading-snug text-white/75 mt-1">{f.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ ROW 1: breeds + about ============ */}
        <section className="mx-auto max-w-[1280px] px-5 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-3 gap-8 lg:gap-9">
          {/* German Shepherds */}
          <article className="bh-card rounded-xl overflow-hidden flex flex-col" style={{ background: "var(--cream2)", boxShadow: "0 22px 55px -28px rgba(14,29,44,.5)" }} data-testid="bh-card-gsd">
            <div className="bh-imgwrap relative h-64 lg:h-72 overflow-hidden" style={{ background: "linear-gradient(180deg, var(--cream) 0%, var(--cream2) 100%)" }}>
              <img src={`${IMG}/02_german_shepherds_group_t.png`} alt="German Shepherds" className="bh-imgzoom absolute inset-0 h-full w-full object-cover object-top" />
            </div>
            <div className="p-7 lg:p-8 flex flex-col flex-1">
              <SectionTitle center={false}>German Shepherds</SectionTitle>
              <p className="font-semibold text-[var(--navy)] text-[15px]">Strong. Intelligent. Loyal.</p>
              <p className="text-[14.5px] leading-relaxed mt-2 mb-6 text-[var(--ink)]/85">
                Meet our 3 beautiful females and our exceptional stud dog.
              </p>
              <Btn variant="navy" testid="bh-view-gsd" className="mt-auto self-start">View German Shepherds</Btn>
            </div>
          </article>

          {/* About */}
          <article className="bh-card rounded-xl p-9 lg:p-10 flex flex-col items-center justify-center text-center" style={{ background: "var(--cream2)", boxShadow: "0 22px 55px -28px rgba(14,29,44,.5)" }} data-testid="bh-card-about">
            <SectionTitle>About Birch Haus</SectionTitle>
            <p className="text-[15px] leading-relaxed text-[var(--ink)]/85">
              Birch Haus is a small, home-based kennel dedicated to preserving the best
              qualities of the <strong className="text-[var(--navy)]">German Shepherd</strong> and{" "}
              <strong className="text-[var(--navy)]">Australian Shepherd</strong>. Our dogs live with
              us in our home, are cherished family members and raised with love, structure and purpose.
            </p>
            <p className="bh-script text-3xl mt-7 mb-7" style={{ color: "var(--navy)" }}>
              Our home. Our dogs. Our family.
            </p>
            <Btn variant="gold" testid="bh-our-story" className="mt-auto">Our Story</Btn>
          </article>

          {/* Australian Shepherds */}
          <article className="bh-card rounded-xl overflow-hidden flex flex-col" style={{ background: "var(--cream2)", boxShadow: "0 22px 55px -28px rgba(14,29,44,.5)" }} data-testid="bh-card-aussie">
            <div className="bh-imgwrap relative h-64 lg:h-72 overflow-hidden" style={{ background: "linear-gradient(180deg, var(--cream) 0%, var(--cream2) 100%)" }}>
              <img src={`${IMG}/03_australian_shepherds_group_t.png`} alt="Australian Shepherds" className="bh-imgzoom absolute inset-0 h-full w-full object-cover object-top" />
            </div>
            <div className="p-7 lg:p-8 flex flex-col flex-1">
              <SectionTitle center={false}>Australian Shepherds</SectionTitle>
              <p className="font-semibold text-[var(--navy)] text-[15px]">Smart. Loving. Versatile.</p>
              <p className="text-[14.5px] leading-relaxed mt-2 mb-6 text-[var(--ink)]/85">
                Meet our 3 amazing girls, raised to be your perfect lifelong companions.
              </p>
              <Btn variant="navy" testid="bh-view-aussie" className="mt-auto self-start">View Australian Shepherds</Btn>
            </div>
          </article>
        </section>

        {/* ============ ROW 2: puppies / socialization / reviews ============ */}
        <section className="mx-auto max-w-[1280px] px-5 lg:px-8 pb-16 lg:pb-24 grid lg:grid-cols-3 gap-8 lg:gap-9">
          {/* Puppies & Planned Litters */}
          <article className="bh-card rounded-xl overflow-hidden flex flex-col" style={{ background: "var(--cream2)", boxShadow: "0 22px 55px -28px rgba(14,29,44,.5)" }} data-testid="bh-card-puppies">
            <div className="bh-imgwrap relative h-64 lg:h-72 overflow-hidden flex items-end justify-center" style={{ background: "linear-gradient(180deg, var(--cream) 0%, var(--cream2) 100%)" }}>
              <img src={`${IMG}/04_puppy_planned_litters_t.png`} alt="Puppy" className="bh-imgzoom h-[94%] w-auto object-contain" style={{ filter: "drop-shadow(0 14px 24px rgba(14,29,44,.22))" }} />
            </div>
            <div className="p-7 lg:p-8 flex flex-col flex-1">
              <h3 className="bh-serif text-[22px] font-bold leading-tight" style={{ color: "var(--navy)" }}>Puppies &amp; Planned Litters</h3>
              <span className="block h-px w-10 my-3" style={{ background: "var(--gold)" }} />
              <p className="text-[14.5px] leading-relaxed mb-6 text-[var(--ink)]/85">Upcoming litters, available puppies and our reservation process.</p>
              <div className="mt-auto flex flex-col sm:flex-row gap-3 items-start">
                <Btn variant="gold" testid="bh-view-puppies" className="!px-5 !py-2.5">View Available Puppies</Btn>
                <Btn variant="outline" testid="bh-planned-litters" className="!px-5 !py-2.5">Planned Litters</Btn>
              </div>
            </div>
          </article>

          {/* Socialization */}
          <article className="bh-card rounded-xl overflow-hidden flex flex-col" style={{ background: "var(--cream2)", boxShadow: "0 22px 55px -28px rgba(14,29,44,.5)" }} data-testid="bh-card-social">
            <div className="bh-imgwrap relative h-64 lg:h-72 overflow-hidden" style={{ background: "var(--cream)" }}>
              <img src={`${IMG}/05_socialization_puppy_tunnel_t.png`} alt="Socialization" className="bh-imgzoom absolute inset-0 h-full w-full object-cover object-center" />
            </div>
            <div className="p-7 lg:p-8 flex flex-col flex-1">
              <h3 className="bh-serif text-[22px] font-bold leading-tight" style={{ color: "var(--navy)" }}>Socialization Program</h3>
              <span className="block h-px w-10 my-3" style={{ background: "var(--gold)" }} />
              <p className="text-[14.5px] leading-relaxed mb-6 text-[var(--ink)]/85">
                We raise confident, well-adjusted puppies through daily socialization, enrichment,
                early handling, grooming, play, car rides and positive experiences.
              </p>
              <Btn variant="navy" testid="bh-learn-program" className="mt-auto self-start">Learn Our Program</Btn>
            </div>
          </article>

          {/* Reviews */}
          <article className="bh-card rounded-xl overflow-hidden flex flex-col" style={{ background: "var(--cream2)", boxShadow: "0 22px 55px -28px rgba(14,29,44,.5)" }} data-testid="bh-card-reviews">
            <div className="bh-imgwrap relative h-64 lg:h-72 overflow-hidden flex items-center justify-center p-6" style={{ background: "linear-gradient(180deg, var(--cream) 0%, var(--cream2) 100%)" }}>
              <img src={`${IMG}/06_reviews_families_collage_t.png`} alt="Happy families" className="bh-imgzoom max-h-full w-auto object-contain" style={{ filter: "drop-shadow(0 12px 22px rgba(14,29,44,.2))" }} />
            </div>
            <div className="p-7 lg:p-8 flex flex-col flex-1">
              <h3 className="bh-serif text-[22px] font-bold leading-tight" style={{ color: "var(--navy)" }}>Reviews &amp; Families</h3>
              <span className="block h-px w-10 my-3" style={{ background: "var(--gold)" }} />
              <p className="text-[14.5px] leading-relaxed mb-6 text-[var(--ink)]/85">
                Read what home families say and see photos of our puppies in their forever homes.
              </p>
              <Btn variant="gold" testid="bh-read-reviews" className="mt-auto self-start">Read Reviews</Btn>
            </div>
          </article>
        </section>

        {/* ============ ROW 3: FAQ / contact / application ============ */}
        <section className="mx-auto max-w-[1280px] px-5 lg:px-8 pb-20 lg:pb-24 grid lg:grid-cols-3 gap-8 lg:gap-9 items-stretch">
          {/* FAQ */}
          <article className="bh-card rounded-xl p-7 lg:p-8 flex flex-col" style={{ background: "var(--cream2)", boxShadow: "0 22px 55px -28px rgba(14,29,44,.5)" }} data-testid="bh-card-faq">
            <h3 className="bh-serif text-[22px] font-bold leading-tight" style={{ color: "var(--navy)" }}>Frequently Asked Questions</h3>
            <span className="block h-px w-10 my-3" style={{ background: "var(--gold)" }} />
            <ul className="space-y-3">
              {["Do you ship puppies?", "What is your reservation process?", "Health guarantee & genetic testing", "Registration & paperwork", "Puppy pick-up & transport in the USA", "Join the waitlist"].map((q) => (
                <li key={q} className="flex items-start gap-2.5 text-[14px] text-[var(--ink)]/85">
                  <PawPrint size={15} className="mt-0.5 shrink-0" style={{ color: "var(--gold)" }} />
                  {q}
                </li>
              ))}
            </ul>
            <div className="bh-imgwrap relative mt-7 h-44 rounded-lg overflow-hidden flex items-end justify-center" style={{ background: "linear-gradient(180deg, var(--cream) 0%, var(--cream2) 100%)" }}>
              <img src={`${IMG}/07_faq_puppy_t.png`} alt="Puppy" className="bh-imgzoom h-[96%] w-auto object-contain" style={{ filter: "drop-shadow(0 12px 20px rgba(14,29,44,.22))" }} />
            </div>
          </article>

          {/* Contact (dark) */}
          <article className="rounded-xl p-10 lg:p-11 flex flex-col items-center justify-center text-center" style={{ background: "var(--navy)" }} data-testid="bh-card-contact">
            <p className="bh-script text-4xl mb-1" style={{ color: "#fff" }}>We're here for you!</p>
            <GoldRule center />
            <p className="text-[14.5px] text-white/75 mb-7">Have questions or ready to apply?<br />We'd love to hear from you.</p>
            <div className="space-y-4 text-[15px] text-white/90">
              <a href="#top" className="flex items-center gap-3 hover:text-white"><Phone size={17} style={{ color: "var(--gold)" }} /> (SSS) 123-4567</a>
              <a href="#top" className="flex items-center gap-3 hover:text-white"><Mail size={17} style={{ color: "var(--gold)" }} /> info@birchhauskennel.com</a>
              <div className="flex items-center gap-3"><MapPin size={17} style={{ color: "var(--gold)" }} /> Proudly located in the USA 🇺🇸</div>
            </div>
          </article>

          {/* Application */}
          <article className="bh-card rounded-xl overflow-hidden flex flex-col" style={{ background: "var(--cream2)", boxShadow: "0 22px 55px -28px rgba(14,29,44,.5)" }} data-testid="bh-card-application">
            <div className="bh-imgwrap relative h-64 lg:h-72 overflow-hidden flex items-end justify-center" style={{ background: "linear-gradient(180deg, var(--cream) 0%, var(--cream2) 100%)" }}>
              <img src={`${IMG}/08_puppy_application_basket_t.png`} alt="Puppy in basket" className="bh-imgzoom h-[94%] w-auto object-contain" style={{ filter: "drop-shadow(0 14px 24px rgba(14,29,44,.22))" }} />
            </div>
            <div className="p-7 lg:p-8 flex flex-col flex-1">
              <h3 className="bh-serif text-[22px] font-bold leading-tight" style={{ color: "var(--navy)" }}>Puppy Application</h3>
              <span className="block h-px w-10 my-3" style={{ background: "var(--gold)" }} />
              <p className="text-[14.5px] leading-relaxed mb-6 text-[var(--ink)]/85">The best homes start with the right match. 🐾</p>
              <Btn variant="gold" icon testid="bh-apply-bottom" className="mt-auto self-start">Apply for a Puppy</Btn>
            </div>
          </article>
        </section>
      </main>

      {/* ============ FOOTER ============ */}
      <footer className="border-t" style={{ borderColor: "rgba(14,29,44,.1)", background: "var(--cream)" }} data-testid="bh-footer">
        <div className="mx-auto max-w-[1280px] px-5 lg:px-8 py-7 flex flex-col md:flex-row items-center justify-between gap-4 text-[12.5px] bh-track-sm uppercase text-[var(--ink)]/70">
          <a href="#top" className="flex items-center gap-2 hover:text-[var(--navy)]"><Instagram size={16} style={{ color: "var(--gold)" }} /> Follow us on Instagram</a>
          <span className="hidden md:inline text-[var(--ink)]/45">Updates • Puppies • Our Daily Life</span>
          <span className="flex items-center gap-2">© 2024 Birch Haus Kennel. All rights reserved. <Heart size={13} style={{ color: "var(--gold)" }} fill="var(--gold)" /></span>
        </div>
      </footer>
        </div>
      </div>
    </div>
  );
}
